import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

import { Simulation } from '@classes/simulation/simulation';
import { Partition } from '@classes/simulation/partition';
import { SimulationService } from '@services/simulation.service';
import { FormatService } from '@services/format.service';
import { NavService } from '@services/nav.service';
import { SimulationFlagType } from '@enums/simulation-flag-type.enum';
import { IndiceModeType } from '@enums/indice-mode-type.enum';
import { NavLink } from '@classes/nav-link';
import { ProviderType } from '@enums/provider-type.enum';
import { CalculatorService } from '@services/calculator.service';

@Component({
    selector: 'app-advanced',
    templateUrl: './advanced.component.html',
    styleUrls: ['./advanced.component.scss']
})
export class AdvancedComponent implements OnInit, OnDestroy {
    private selectedPartSub: Subscription;
    private simulationValSub: Subscription;
    private projRateSub: Subscription;

    simulation: Simulation;
    selectedPartition: Partition;
    selectedPartitionBkp: Partition;
    partitionsBkp: Partition[];
    partitionsTree: Partition[];
    mainPartition: Partition;
    breadcrumbArray: string[];
    paramLink: NavLink;
    backLink: NavLink;
    previousYear: number;

    isEdited = false;

    ProviderType = ProviderType;

    constructor(
        private route: ActivatedRoute,
        private simulationSrv: SimulationService,
        private formatSrv: FormatService,
        private navSrv: NavService,
        private calcSrv: CalculatorService
    ) { }

    ngOnInit() {
        this.initSubscriptions();
        this.configureLinks();

        this.previousYear = this.simulation.partitions[0].simulationValues.years.reverse().find(y => !y.isEstimated).year;
    }

    onValidate(): void {
        this.isEdited = false;
        this.removeChildrenParts();
        this.simulationSrv.calculate(this.simulation);
    }

    onReset() {
        this.selectedPartition.simulationValues = JSON.parse(JSON.stringify(this.selectedPartitionBkp.simulationValues));
        this.selectedPartition.projectionRate = JSON.parse(JSON.stringify(this.selectedPartitionBkp.projectionRate));
        this.selectedPartition.cursorParameters = JSON.parse(JSON.stringify(this.selectedPartitionBkp.cursorParameters));

        this.resetChildrenParts(this.selectedPartition);

        if(this.simulation.isMJP) {
            this.calcSrv.calculate(this.simulation, this.selectedPartition.partitionDetails.code);
        }

        this.simulationSrv.selectedPartitionChanged.next(this.selectedPartition);
    }

    private configureLinks(): void {
        this.backLink = this.navSrv.getLinkFromRoute(['simulation', 'live'], 'close');
        this.paramLink = this.navSrv.getLinkFromRoute(['parameters', 'config-simulation'], 'settings');
    }

    private resetChildrenParts(parentPart: Partition): void {
        this.simulation.partitions.map((partition, i) => {
            if(partition.parentCode === parentPart.partitionDetails.code &&
                partition.simulationValues.flag !== SimulationFlagType.modified) {

                const partitionBkp = this.partitionsBkp.find(p => p.partitionDetails.code === partition.partitionDetails.code);
                if (partitionBkp) {
                    partition.simulationValues = JSON.parse(JSON.stringify(partitionBkp.simulationValues));
                    partition.projectionRate =  JSON.parse(JSON.stringify(partitionBkp.projectionRate));
                    partition.cursorParameters =  JSON.parse(JSON.stringify(partitionBkp.cursorParameters));
                } else {
                    this.simulation.partitions.splice(i, 1);
                }

                this.resetChildrenParts(partition);
            }
        });
    }

    // Remove children parts created due to the use of partitionsArrayToTree
    private removeChildrenParts(): void {
        this.simulation.partitions.map(p => {
            p.partitions = [];
        });
    }

    private initSubscriptions(): void {
        this.route.data
        .subscribe(data => {
            this.simulation = JSON.parse(JSON.stringify(data.simulation));
            this.partitionsTree = this.formatSrv.partitionsArrayToTree(this.simulation.partitions);
            this.partitionsBkp = this.simulationSrv.backupSimu.partitions;
            this.selectPartition(this.simulationSrv.selPartition || this.simulation.partitions[1]);
        });

        this.selectedPartSub = this.simulationSrv.selectedPartitionChanged
            .subscribe(selectedPart => {
                this.selectPartition(selectedPart);
            });

        this.simulationValSub = this.simulationSrv.simulationValChanged
            .subscribe(simulationValues => {
                this.selectedPartition.simulationValues = simulationValues;
                this.updateSelectedPartStatus();
                if(this.simulation.isMJP) {
                    this.calcSrv.calculate(this.simulation, this.selectedPartition.partitionDetails.code);
                }
            });

        this.projRateSub = this.simulationSrv.projectionRateChanged
            .subscribe(projectionRate => {
                this.selectedPartition.projectionRate = projectionRate;
                this.updateSelectedPartStatus();
                if(this.simulation.isMJP) {
                    this.calcSrv.calculate(this.simulation, this.selectedPartition.partitionDetails.code);
                }
            });
    }

    private selectPartition(selectedPart: Partition): void {
        this.selectedPartition = this.simulation.partitions.find(p => p.partitionDetails.code === selectedPart.partitionDetails.code);
        this.selectedPartitionBkp = this.partitionsBkp.find(p => p.partitionDetails.code === selectedPart.partitionDetails.code);

        const filteredPartitions = this.simulation.partitions
            .filter(p => p.partitionDetails.articleType === this.selectedPartition.partitionDetails.articleType);
        this.mainPartition = this.formatSrv.partitionsArrayToTree(filteredPartitions)[0];
        this.setBreadcrumb();
    }

    private setBreadcrumb(): void {
        this.breadcrumbArray = [];
        const partitions = this.simulation.partitions;
        let selectedPart = this.selectedPartition;

        while (selectedPart) {
            this.breadcrumbArray.unshift(selectedPart.partitionDetails.text.shortLabel);
            selectedPart = partitions.find(p => p.partitionDetails.code === selectedPart.parentCode);
        }
    }

    private updateSelectedPartStatus(): void {
        this.selectedPartition.simulationValues.flag = SimulationFlagType.modified;

        this.updateChildrenPartStatus(this.selectedPartition);

        this.isEdited = true;
    }

    private updateChildrenPartStatus(selPartition: Partition): void {
        const selYears = this.selectedPartition.simulationValues.years;

        this.simulation.partitions.map(partition => {
            if(partition.parentCode === selPartition.partitionDetails.code
                && partition.simulationValues.flag !== SimulationFlagType.modified
                && partition.simulationValues.flag !== SimulationFlagType.virtual
                && selPartition.simulationValues.mode === IndiceModeType.percent) {

                partition.projectionRate = JSON.parse(JSON.stringify(selPartition.projectionRate));
                partition.cursorParameters = JSON.parse(JSON.stringify(selPartition.cursorParameters));
                partition.simulationValues.flag = SimulationFlagType.inherited;
                partition.simulationValues.indice = selPartition.simulationValues.indice;
                partition.simulationValues.mode = selPartition.simulationValues.mode;

                partition.simulationValues.years.map((year, i) => {
                    year.flag = selYears[i].flag;
                    year.mode = selYears[i].mode;
                    year.indice = selYears[i].indice;
                    year.isRateNeutralized = selYears[i].isRateNeutralized;

                    if(this.simulation.isMJP && selYears[i].amount === 0) {
                        year.isAmountEdited = true;
                        year.amount = 0;
                    }
                });

                this.updateChildrenPartStatus(partition);
            }
        });
    }

    ngOnDestroy() {
        this.removeChildrenParts();

        if(this.selectedPartSub) {
            this.selectedPartSub.unsubscribe();
        }

        if(this.simulationValSub) {
            this.simulationValSub.unsubscribe();
        }

        if(this.projRateSub) {
            this.projRateSub.unsubscribe();
        }
    }
}
